/**
 * Which shell the app is running inside.
 *
 * The same bundle serves the website and the installed iOS/Android app, but the
 * two want different first screens. On the web, `/` is a marketing page whose
 * job is to convince a stranger to sign up. In an installed app that job is
 * already done — the user found the app, installed it and opened it — so the
 * landing page is a wall between them and their account. The native shell goes
 * straight to sign-in.
 *
 * Detection is deliberately belt-and-braces. `VITE_APP_SHELL=native` is the
 * explicit signal the native build sets, so behaviour is a build decision that
 * can be reasoned about and tested. The Tauri globals are the runtime fallback,
 * which keeps `tauri dev` (pointing at a plain `npm run dev` server that has no
 * such env var) behaving like the app it is.
 */
export type AppShell = 'web' | 'native'

type TauriWindow = typeof globalThis & {
  __TAURI_INTERNALS__?: unknown
  __TAURI__?: unknown
  isTauri?: boolean
}

export const appShell = (): AppShell => {
  if (import.meta.env.VITE_APP_SHELL === 'native') return 'native'
  if (typeof window === 'undefined') return 'web'
  const w = window as TauriWindow
  return w.__TAURI_INTERNALS__ !== undefined || w.__TAURI__ !== undefined || w.isTauri === true
    ? 'native'
    : 'web'
}

/** True inside the packaged iOS/Android/desktop shell. */
export const isNativeShell = (): boolean => appShell() === 'native'
